// axiosInstance.ts (refreshed with TokenManager integration)
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { tokenManager } from '@/lib/TokenManager';
import { clearAllAppStorage } from '@/utils/storage/storageUtils.ts';
import { logAuthRequest, logAuthResponse, logFatalAuthError, logTokenClear } from '@/utils/auth/logging';

const apiUrl = import.meta.env.VITE_API_URL;
const axiosInstance = axios.create({
	baseURL: apiUrl,
	timeout: 60 * 1000 * 2,
});

const RETRIED_FLAG = 'x-auth-retried';

// Request: attach valid access token (skip auth endpoints)
axiosInstance.interceptors.request.use(
	async (config: InternalAxiosRequestConfig) => {
		const fullUrl = ((config.baseURL ?? '') + (config.url ?? '')).toLowerCase();
		if (fullUrl.includes('/token') || fullUrl.includes('/google-token')) {
			return config;
		}

		const token = await tokenManager.getValidAccessToken();
		if (token) {
			config.headers = config.headers ?? {};
			config.headers['Authorization'] = `Bearer ${token}`;
		}
		logAuthRequest(config.url ?? 'unknown', !!token);
		return config;
	},
	(error) => Promise.reject(error),
);

// Response: handle RFC 6750 401 -> refresh once -> retry
axiosInstance.interceptors.response.use(
	(response) => response,
	async (error: AxiosError<{ code?: string; detail?: string }>) => {
		const status = error.response?.status;
		if (status !== 401) return Promise.reject(error);

		const request = error.config as InternalAxiosRequestConfig & { [RETRIED_FLAG]?: boolean };
		const fullUrl = ((request.baseURL ?? '') + (request.url ?? '')).toLowerCase();
		const url = request.url ?? 'unknown';

		// Do not attempt refresh for auth endpoints
		if (fullUrl.includes('/token') || fullUrl.includes('/google-token')) {
			return handleFatalLogout(error);
		}

		// Already retried once
		if (request[RETRIED_FLAG]) {
			logAuthResponse(status, url, false);
			return handleFatalLogout(error);
		}

		const data = error.response?.data as { code?: string; detail?: string } | undefined;
		const code = data?.code;

		if (code === 'ACCESS_TOKEN_INVALID' || !code) {
			logAuthResponse(status, url, true);
			const newAccess = await tokenManager.getValidAccessToken();
			if (!newAccess) return handleFatalLogout(error);

			request.headers = request.headers ?? {};
			request.headers['Authorization'] = `Bearer ${newAccess}`;
			request[RETRIED_FLAG] = true;
			try {
				return await axiosInstance.request(request);
			} catch (e) {
				return handleFatalLogout(e as AxiosError);
			}
		}

		if (code === 'REFRESH_TOKEN_INVALID' || code === 'USER_NOT_ACTIVE' || code === 'INVALID_CREDENTIALS') {
			logFatalAuthError(code, url);
			return handleFatalLogout(error);
		}

		logFatalAuthError(code || 'UNKNOWN', url);
		return handleFatalLogout(error);
	},
);

/**
 * Handles fatal authentication errors by clearing all tokens and app data
 * Uses centralized storage utilities to ensure complete cleanup
 */
function handleFatalLogout(error: AxiosError) {
	try {
		// Clear tokens via TokenManager (handles token-specific storage)
		logTokenClear('fatal_error');
		tokenManager.clear();

		clearAllAppStorage();
	} finally {
		if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
			window.location.href = '/login';
		}
	}
	return Promise.reject(error);
}

export default axiosInstance;
